import AgreeabilityScale from "components/AgreeabilityScale";
import DateSelection from "components/DateSelection";
import DateTimeSelection from "components/DateTimeSelection";
import TimeSelection from "components/TimeSelection";
import ImageResponse from "components/ImageResponse";
import MultiSelectDropdown from "components/MultiSelectDropdown";
import NumberInput from "components/NumberInput";
import SimpleDropdown from "components/SimpleDropdown";
import SingleChoiceButton from "components/SingleSelectButton";
import SingleSelectDropDown from "components/SingleSelectDropdown";
import PhoneNumberInput from "components/PhoneNumberInput";
import TextBox from "components/TextBox";
import Video from "components/Video";
import { get, isEmpty, kebabCase } from 'lodash';
import React from 'react';
import PasswordInput from "../PasswordInput";
import TextInput from "../TextInput";
import QuestionWrapper from "./QuestionWrapper";
import ImageFileUpload from "components/ImageFileUpload";
import Scale from "components/Scale";
import DocumentUpload from "../DocumentUpload";

// `describedBy` is pulled out of the spread: the widgets built on MUI's
// TextField forward whatever they are given to the DOM, and only these two
// place it on the input itself.
const QuestionWidget = ({describedBy, ...props}) => {
  const { centerOptionText, height, variant } = props;

  switch (props.question.questionType) {
    case 'Text':
      return (<TextInput {...props} autoComplete={props.question.autocomplete}  showBorder={props.showBorder} describedBy={describedBy}/>);
    case 'Password':
      return (<PasswordInput {...props} describedBy={describedBy}/>);
    case 'Number':
      return (<NumberInput {...props} autoComplete={props.question.autocomplete}/>);
    case 'PhoneNumber':
      return (<PhoneNumberInput {...props} autoComplete={props.question.autocomplete} describedBy={describedBy}/>);
    case 'TextBox':
      return <TextBox {...props} height={height} />;
    case 'SingleChoiceRadio':
      return <SingleChoiceButton {...props} centerOptionText={centerOptionText} showBorder={props.showBorder}/>;
    case 'SingleChoiceDropDown':
      return <SingleSelectDropDown {...props} DropDownItems={props.question.answerOptions} showBorder={props.showBorder}/>;
    case 'SimpleDropDown':
      return <SimpleDropdown {...props} DropDownItems={props.question.answerOptions}/>;
    case 'MultiChoiceDropDown':
      return <MultiSelectDropdown {...props} DropDownItems={props.question.answerOptions} showBorder={props.showBorder} maxSelections={props.question.maxSelections} minSelections={props.question.minSelections} variant={variant}/>;
    case 'SingleChoiceButton':
      return <SingleChoiceButton {...props} showBorder={props.showBorder}/>;
    case 'Date':
      return <DateSelection {...props} showBorder={props.showBorder} openTo={props.question.openTo}/>;
    case 'DateTime':
      return <DateTimeSelection {...props}/>;
    case 'Time':
      return <TimeSelection {...props} showBorder={props.showBorder}/>;
    case 'AgreeabilityThreeOptions':
      return <AgreeabilityScale {...props} maximumValue={3}/>;
    case 'AgreeabilityFiveOptions':
      return <AgreeabilityScale {...props} maximumValue={5}/>;
    case 'AgreeabilitySevenScale':
      return <AgreeabilityScale {...props} maximumValue={7}/>;
    case 'AgreeabilityTenScale':
      return <AgreeabilityScale {...props} maximumValue={10}/>;
    case 'Scale':
      return <Scale {...props}/>;
    case 'ImageResponse':
      return <ImageResponse {...props}/>;
    case 'SingleImageUpload':
      return <ImageFileUpload {...props}/>;
    case 'YoutubeVideo':
      return (<Video videoId={get(props.question, 'answerOptions[0].key')} />);
    case 'Document':
      return <DocumentUpload {...props} />;

    default:
      return (<p>Can not render! {props.question.questionType}</p>);
  }
};

// A question needs one id so its label, its error and its help text can point
// at the field. Survey and quiz questions carry a key; the ones built inline in
// signup and the auth screens are identified by their label instead.
const idFor = question => `q-${question.key || kebabCase(question.label) || 'field'}`;

function Question(props) {
  const {
    question,
    onAnswered,
    value,
    labelStyling = {},
    labelSpacing,
    error,
    centerOptionText = false,
    height,
    disabled,
    showBorder = false,
    variant,
  } = props;

  const inputId = idFor(question);
  const errorId = `${inputId}-error`;
  const helpId = `${inputId}-help`;
  const describedBy = [
    !isEmpty(error) && errorId,
    !isEmpty(question.helpText) && helpId,
  ].filter(Boolean).join(' ');

  return (
    <QuestionWrapper
      question={question}
      labelStyling={labelStyling}
      labelSpacing={labelSpacing}
      error={error}
      disabled={disabled}
      inputId={inputId}
      errorId={errorId}
      helpId={helpId}
    >
      <QuestionWidget
        question={question}
        value={value}
        onAnswered={onAnswered}
        error={error}
        centerOptionText={centerOptionText}
        height={height}
        disabled={disabled}
        showBorder={showBorder}
        variant={variant}
        id={inputId}
        describedBy={describedBy}
      />
    </QuestionWrapper>
  );
}

export default Question;
